// ============================================
// COMPONENT - Modal detalle de un turno (abierto o cerrado)
// ============================================

import { useState } from 'react';
import { useTurnoDetalle, useMovimientosDeTurno } from '@almacenes/core';
import { Spinner } from '@almacenes/ui';
import { MdClose, MdPrint } from 'react-icons/md';
import { formatCurrency, formatearFechaHora } from '../utils/formato';
import { badgeEstadoMovimiento } from '../utils/badges';
import { PaginacionTabla } from './PaginacionTabla';
import { TableSkeletonRows } from './TableSkeletonRows';
import { DesglosePorMedioPago } from './DesglosePorMedioPago';
import { KpiTile } from './KpiTile';
import { usePrintComprobanteTurno } from '../../../hooks/usePrintComprobanteTurno';

interface TurnoDetalleModalProps {
  turnoId: number;
  onClose: () => void;
}

export const TurnoDetalleModal = ({ turnoId, onClose }: TurnoDetalleModalProps) => {
  const { data: turno, isLoading: isLoadingTurno, isFetching: isFetchingTurno } = useTurnoDetalle(turnoId);
  const { imprimirComprobante, imprimiendo } = usePrintComprobanteTurno();
  const [page, setPage] = useState(0);
  const { data: movimientos, isLoading: isLoadingMovimientos, isFetching: isFetchingMovimientos } = useMovimientosDeTurno(turnoId, page);
  // Refetch en segundo plano (no la carga inicial): atenúa mientras se actualiza para que
  // el cambio no se sienta como un salto brusco.
  const actualizandoTurno = isFetchingTurno && !isLoadingTurno;
  const actualizandoMovimientos = isFetchingMovimientos && !isLoadingMovimientos;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#11194F' }}>
            Detalle del turno
            {actualizandoTurno && <Spinner size="sm" />}
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => turno && imprimirComprobante(turno)}
              disabled={!turno || imprimiendo}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: '#11194F' }}
            >
              {imprimiendo ? <Spinner size="sm" /> : <MdPrint className="text-lg" />}
              Imprimir comprobante
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <MdClose className="text-2xl" />
            </button>
          </div>
        </div>

        {isLoadingTurno || !turno ? (
          <div className="flex justify-center py-12"><Spinner size="lg" /></div>
        ) : (
          <div className={`space-y-4 transition-opacity duration-300 ${actualizandoTurno ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Abierto el {formatearFechaHora(turno.fechaHoraApertura)}
                {turno.fechaHoraCierre && <> · Cerrado el {formatearFechaHora(turno.fechaHoraCierre)}</>}
              </p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  turno.estado === 'ABIERTO' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {turno.estado}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiTile variant="compact" label="Monto inicial" value={formatCurrency(turno.montoInicial)} />
              <KpiTile variant="compact" label="Total ventas" value={formatCurrency(turno.totalVentas)} />
              <KpiTile variant="compact" label="Ingresos (todos los medios)" value={formatCurrency(turno.totalIngresos)} />
              {turno.totalIngresosEfectivo != null && (
                <KpiTile variant="compact" label="Ingresos en efectivo" value={formatCurrency(turno.totalIngresosEfectivo)} />
              )}
              <KpiTile variant="compact" label="Egresos" value={formatCurrency(turno.totalEgresos)} />
              <KpiTile variant="compact" label="Efectivo esperado" value={formatCurrency(turno.efectivoEsperado)} highlight />
              {turno.arqueoRealizado && (
                <KpiTile
                  variant="compact"
                  label="Diferencia de arqueo"
                  value={formatCurrency(turno.diferenciaEfectivo)}
                  highlight={(turno.diferenciaEfectivo ?? 0) >= 0}
                />
              )}
            </div>

            {turno.motivoCierre && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800"><strong>Motivo de cierre:</strong> {turno.motivoCierre}</p>
              </div>
            )}

            <DesglosePorMedioPago turno={turno} />

            <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity duration-300 ${actualizandoMovimientos ? 'opacity-60' : ''}`}>
              <h4 className="text-sm font-bold px-4 py-3 border-b border-gray-200" style={{ color: '#11194F' }}>
                Movimientos del turno
              </h4>
              {!isLoadingMovimientos && (!movimientos || movimientos.content.length === 0) ? (
                <p className="text-center py-6 text-sm text-gray-500">No hay movimientos registrados</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F3F4F9]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-bold text-[#11194F]">Tipo</th>
                          <th className="px-4 py-2 text-left text-xs font-bold text-[#11194F]">Glosa</th>
                          <th className="px-4 py-2 text-right text-xs font-bold text-[#11194F]">Monto</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-[#11194F]">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingMovimientos ? (
                          <TableSkeletonRows columns={4} rows={3} />
                        ) : (
                          movimientos!.content.map((m, index) => (
                            <tr key={m.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-2 text-sm">{m.tipo}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{m.glosa}</td>
                              <td className="px-4 py-2 text-sm text-right">{formatCurrency(m.monto)}</td>
                              <td className="px-4 py-2 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${badgeEstadoMovimiento(m.estado)}`}>
                                  {m.estado}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {movimientos && (
                    <PaginacionTabla
                      page={page}
                      totalPages={movimientos.totalPages}
                      totalElements={movimientos.totalElements}
                      cantidadEnPagina={movimientos.content.length}
                      etiqueta="movimientos"
                      onChange={setPage}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
